import { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { themed } from './themed';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Input } from './Input';
import { Checkbox } from './checkbox';
import { Button } from './Button';

export interface MultiSelectItem {
  id: string;
  label: string;
}

export interface MultiSelectPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  items: MultiSelectItem[];
  selected: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  searchPlaceholder?: string;
}

const COLOR_VARIANTS = [
  'border-brand-200 bg-brand-50 text-brand-700',
  'border-mint-200 bg-mint-50 text-mint-700',
  'border-amber-200 bg-amber-50 text-amber-700',
  'border-coral-200 bg-coral-50 text-coral-700',
  'border-cyan-200 bg-cyan-50 text-cyan-700',
  'border-violet-200 bg-violet-50 text-violet-700',
  'border-rose-200 bg-rose-50 text-rose-700',
  'border-teal-200 bg-teal-50 text-teal-700',
  'border-brand-300 bg-brand-100 text-brand-800',
  'border-emerald-200 bg-emerald-50 text-emerald-700',
] as const;

const DOT_COLORS = [
  'bg-brand-500',
  'bg-mint-500',
  'bg-amber-500',
  'bg-coral-500',
  'bg-cyan-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-brand-600',
  'bg-emerald-500',
] as const;

function getItemColor(index: number): { chip: string; dot: string } {
  const i = index % COLOR_VARIANTS.length;
  return { chip: COLOR_VARIANTS[i]!, dot: DOT_COLORS[i]! };
}

export function MultiSelectPopup({
  open,
  onOpenChange,
  title,
  description,
  items,
  selected,
  onSelectionChange,
  searchPlaceholder = 'Search…',
}: MultiSelectPopupProps): JSX.Element {
  const [draft, setDraft] = useState<Set<string>>(new Set(selected));
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(new Set(selected));
      setSearch('');
    }
  }, [open, selected]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [items, search]);

  function toggle(id: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleDone() {
    onSelectionChange(Array.from(draft));
    onOpenChange(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) setDraft(new Set(selected));
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-secondary)]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {filtered.length === 0 ? (
            <div className={cn('text-sm py-8 text-center', themed.text.secondary)}>
              {search ? 'No matching items found' : 'No items available'}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {filtered.map((item, idx) => {
                const checked = draft.has(item.id);
                const originalIndex = items.findIndex((i) => i.id === item.id);
                const { chip, dot } = getItemColor(originalIndex >= 0 ? originalIndex : idx);
                return (
                  <label
                    key={item.id}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all select-none',
                      checked ? `${chip} border-current/40 shadow-sm` : 'border-surface-200 hover:border-surface-300 hover:bg-surface-50',
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(item.id)}
                      className="flex-none"
                    />
                    <span className={cn('size-2 rounded-full flex-none', dot)} />
                    <span className="text-sm font-medium text-ink-800 leading-tight truncate">{item.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <span className="text-xs text-[var(--text-secondary)]">
            {draft.size} selected
          </span>
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
